import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { ENVIRONMENT } from '@sona/angular-util-environment';
import { RouteHandler } from '@sona/angular-util-router';
import { isFunction } from 'lodash';

import { SUPABASE } from '../../tokens/supabase.token';

/**
 * The options for the terms guard.
 */
export interface ITermsGuardOptions {
    /**
     * A handler that is applied when the user has already accepted the current terms. Defaults to `true`.
     *
     * @default `true`
     */
    currentTermsAccepted?: boolean | RouteHandler;

    /**
     * A handler that is applied when the user has not yet accepted the current terms. Defaults to `false`.
     *
     * @default `false`
     */
    currentTermsNotAccepted?: boolean | RouteHandler;
}

/**
 * A guard that checks if the user has accepted the current terms.
 * By default, will allow access to the route if the user has accepted the current terms and deny access if the user has not accepted the current terms.
 *
 * @param options
 */
export const termsGuard =
    (options?: ITermsGuardOptions): CanActivateFn =>
        async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
            const router = inject(Router);
            const supabase = inject(SUPABASE);
            const environment = inject(ENVIRONMENT);

            const { termsAcceptedAt, termsAcceptedVersion } = (await supabase.auth.getUser()).data?.user?.app_metadata ?? {};

            const applyHandler = (handler?: boolean | RouteHandler) =>
                isFunction(handler) ? handler(route, state) : handler;

            if (!termsAcceptedAt || termsAcceptedVersion < environment.legal.terms.version) {
                return applyHandler(options?.currentTermsNotAccepted) ?? false;
            }

            return applyHandler(options?.currentTermsAccepted) ?? true;
        };
